/** FUNDS — the scheme master, ownership tiers, and one security's record. */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import {
  GET_ALL_MUTUAL_FUNDS, SEARCH_MUTUAL_FUNDS, SCREENER_MUTUAL_FUNDS, GET_FUND, GET_FUND_METADATA,
  ALPHA_FOR_FUND, MF_PRICE_HISTORY, MF_TRANSACTIONS_HISTORY, GET_MY_CUSTOM_SCREENS, FUNDS_BY_SYMBOLS, ETF_AND_MODEL_NAMES,
} from '../queries';
import { GET_ASSET_CLASSES_WITH_SEBI } from '../queries/models';
import { SECURITY_KIND } from '../../lib/security';
import { DEFAULT_ALPHA_HORIZON } from '../../lib/alpha';
import { TRADE_ACTION } from '../../lib/trading';
import { useBook } from './book';
import { useAlphaStatus } from './alpha';
import { useOrganization } from './firm';
import { EMPTY, isClientBook } from './_shared';
import { debug } from '../../lib/debug';
import { FACTOR_FIELDS } from '../../lib/factors';

/**
 * The scheme universe, optionally searched and filtered.
 *
 * `searchQuery` and `category` are ANDed by the resolver, which matters more
 * than it sounds: a search sent with a category attached looks up seven Parag
 * Parikh schemes and answers with the one in that category. A caller running a
 * search across the whole master passes `category: null` deliberately.
 *
 * `skip` is for the typeahead — a box with nothing typed in it would otherwise
 * fetch the unfiltered first page on every mount. `offset` pages through the
 * matches `limit` at a time; `total` is the count across every page.
 */
export function useFunds({ search, category, assetGroup, limit = 40, offset = 0, skip = false } = {}) {
  const { data, loading, error } = useQuery(SEARCH_MUTUAL_FUNDS, {
    variables: {
      searchQuery: search || null,
      category: category || null,
      assetGroup: assetGroup || null,
      limit,
      offset,
    },
    skip,
    fetchPolicy: 'cache-first',
  });
  return {
    loading,
    error,
    funds: data?.getAllMutualFunds?.funds ?? EMPTY,
    total: data?.getAllMutualFunds?.total ?? 0,
  };
}

/**
 * The schemes of an agent-built screen, in the row shape `useFunds` returns so
 * the screener table renders either. Skipped with no id. Network-first: the
 * same id always names the same list, but its figures are read fresh.
 *
 * A screen handle can come from either of two tools, and they do NOT share a
 * store. `screen_mutual_funds` writes `bolodhan:mf_screener:<id>`, which
 * `getScreenerMutualFunds` reads; the screen builder behind
 * `UPDATE_SCREEN_BUILDER_RESULT` writes `bolodhan:c_s_b:<id>`, which only
 * `getMyCustomScreens(urlId:)` reads. The ids are indistinguishable UUIDs and
 * the agent sends both to this one `url_id` param, so a builder screen asked
 * of the screener store came back `count: 0` and the reader was shown an empty
 * table while the agent said it had found twenty funds.
 *
 * So: ask the screener store first, and fall back to the builder store only
 * when it answered with nothing. One extra request, and only on the handles
 * that would otherwise render blank. The builder's rows carry the symbol, the
 * name and the asset class and no figures — the table prints those as '—',
 * which is the honest reading of "this list is the screen; its numbers are
 * not in it".
 */
export function useScreenerFunds(urlId) {
  const { data, loading, error } = useQuery(SCREENER_MUTUAL_FUNDS, {
    variables: { urlId: urlId || '' },
    skip: !urlId,
    fetchPolicy: 'cache-and-network',
  });

  /* Only once the screener store has actually answered, and answered empty —
     asking both every time would double the requests for the common case. */
  const screenerAnswered = Boolean(data);
  const screenerEmpty = screenerAnswered && !(data?.getScreenerMutualFunds?.securities ?? EMPTY).length;
  const builder = useQuery(GET_MY_CUSTOM_SCREENS, {
    variables: { screenType: null, urlId: urlId || '' },
    skip: !urlId || !screenerEmpty,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const rows = data?.getScreenerMutualFunds?.securities;
  const builderRows = builder.data?.getMyCustomScreens?.[0]?.results;

  const funds = useMemo(() => {
    if ((rows ?? EMPTY).length) {
      return rows.map((s) => ({
        securitySymbol: s.securitySymbol,
        schemeName: s.fundStandardName || s.schemeName,
        fundName: s.schemeName,
        fundHouse: s.fundHouse,
        fundLevelCategoryName: s.category,
        starRating: s.starRating,
        nav: s.nav,
        aum: s.aum,
        expenseRatio: s.expenseRatio,
      }));
    }
    /* The builder's own row shape, mapped onto the same fields. `assetClass` is
       the closest thing it carries to a category; the figures it has none of
       are left null so the table prints '—' rather than a zero. */
    return (builderRows ?? EMPTY).map((r) => ({
      securitySymbol: r.symbol,
      schemeName: r.name || r.symbol,
      fundName: r.name,
      fundHouse: null,
      fundLevelCategoryName: r.assetClass,
      starRating: null,
      nav: null,
      aum: null,
      expenseRatio: null,
    }));
  }, [rows, builderRows]);

  const source = (rows ?? EMPTY).length ? 'screener' : funds.length ? 'builder' : null;

  /* Which store answered a handle is the single fact that explains an empty
     screener table, a save that is refused, and a save that is allowed — and
     it is invisible from the outside, because the two handles are both bare
     UUIDs. Traced once per resolution so the console says which namespace the
     id came from instead of leaving it to be inferred. */
  useEffect(() => {
    if (!urlId || loading || (screenerEmpty && builder.loading)) return;
    debug('screener → url_id resolved', {
      urlId,
      source: source || 'neither',
      rows: funds.length,
      screenerCount: data?.getScreenerMutualFunds?.count ?? 0,
      builderTried: screenerEmpty,
      builderRows: (builderRows ?? EMPTY).length,
      savable: source === 'builder',
      note: source === 'screener'
        ? 'screen_mutual_funds handle (bolodhan:mf_screener:) — renders, but saveScreen cannot keep it'
        : source === 'builder'
          ? 'screen builder handle (bolodhan:c_s_b:) — renders and can be saved'
          : 'neither store knows this id: expired (1h TTL), or it was never real',
    });
  }, [urlId, source, loading, screenerEmpty, builder.loading, funds.length, builderRows, data]);

  return {
    loading: Boolean(urlId) && ((loading && !data) || (screenerEmpty && builder.loading && !builder.data)),
    error: error || (screenerEmpty ? builder.error : undefined),
    funds: urlId ? funds : EMPTY,
    total: (data?.getScreenerMutualFunds?.count ?? 0) || funds.length,
    /** Which store answered — the screener's, the builder's, or neither. */
    source,
  };
}

/**
 * The scheme master, filtered in the browser.
 *
 * `getAllMutualFunds` takes no `fundHouse` or `managerName` argument, so the
 * whole list is fetched once (cache-first, one request per session) and the
 * filtering happens here. That is the only way a dropdown can offer every
 * house rather than only the houses that happen to be on the page in hand.
 *
 * `hasManagerData` reports whether the API returned any manager at all, so the
 * control can explain itself instead of offering an empty list. It reads the
 * raw rows rather than the derived list, which a house selection can legitimately
 * narrow to nothing.
 */
export function useFundCatalogue({ house = null, manager = null, search = '', limit = 4000 } = {}) {
  const { data, loading, error } = useQuery(GET_ALL_MUTUAL_FUNDS, {
    variables: { limit },
    fetchPolicy: 'cache-first',
  });

  const all = data?.getAllMutualFunds?.funds ?? EMPTY;

  /**
   * Each SEBI category's average one-year return, derived — the fallback only.
   *
   * The backend publishes the real figure as `snapshot.categoryReturn1yr` and
   * `categoryAverage1yr` below takes that first; it currently answers on every
   * row. This exists for the rows where it does not, so a missing peer figure
   * costs one fund its comparison rather than emptying the column.
   *
   * It is a poorer measure than the published one, which is why it loses: the
   * backend's peer group is finer than the SEBI label these rows carry — the
   * 319 "Index Fund" schemes resolve to 24 different peer returns, because a
   * Nifty 50 tracker is not measured against a Nasdaq feeder. Averaging by
   * category name flattens exactly that distinction.
   *
   * `getMutualFundCategoryStats` is not the fallback, despite the name: it
   * covers ten marketing buckets ("Flexi Cap", "Top Funds", "SIP < 500") whose
   * names match none of the 30 SEBI categories on these rows.
   *
   * Over `all` and memoised apart from the filters, because a fund's standing
   * against its category is a fact about the fund: it must not shift when
   * someone picks a house, and must not be recounted on every keystroke.
   */
  const derivedCategoryReturn1yr = useMemo(() => {
    const sums = new Map();
    all.forEach((f) => {
      if (!f.category || f.return1yr == null) return;
      const cur = sums.get(f.category) ?? { total: 0, count: 0 };
      cur.total += Number(f.return1yr);
      cur.count += 1;
      sums.set(f.category, cur);
    });
    return new Map([...sums].map(([category, s]) => [category, s.total / s.count]));
  }, [all]);

  return useMemo(() => {
    const managersOf = (f) => (f.managers ?? EMPTY).map((m) => m.managerName).filter(Boolean);
    const byHouse = (f) => !house || f.fundHouse === house;
    const byManager = (f) => !manager || managersOf(f).includes(manager);

    /* Every word has to appear somewhere in the row, so "bandhan small" finds
       the small cap fund without the words having to be adjacent or in order. */
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const bySearch = (f) => {
      if (!words.length) return true;
      const hay = `${f.schemeName || ''} ${f.fundStandardName || ''} ${f.fundHouse || ''} ${f.category || ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    };

    /**
     * Each list is narrowed by the *other* selection, never by its own.
     *
     * Pick a house and the manager list becomes the people who run that house's
     * schemes; pick a manager and the house list becomes the houses they work
     * for. Narrowing a list by its own selection would leave it holding one
     * entry — the thing already chosen — and no way back.
     */
    const houses = [...new Set(all.filter(byManager).map((f) => f.fundHouse).filter(Boolean))].sort();
    const managerNames = [...new Set(all.filter(byHouse).flatMap(managersOf))].sort();

    /* Text narrows the rows but never the dropdowns: a house you can no longer
       see because of what you typed is exactly the one you may want to pick. */
    const funds = all.filter((f) => byHouse(f) && byManager(f) && bySearch(f));

    return {
      loading,
      error,
      /** Every scheme, after the selected filters. */
      funds,
      /**
       * What a scheme's peer group returned over a year.
       *
       * The backend's published figure, falling back to the derived average.
       * The two are not the same number — Aggressive Hybrid is published at
       * 5.37 against the 4.87 these rows average to — so the order matters, and
       * every screen asks through here precisely so the deep-dive and the table
       * cannot end up quoting different figures for the same fund.
       */
      categoryAverage1yr: (f) => {
        const published = f?.snapshot?.categoryReturn1yr;
        if (published != null) return Number(published);
        return derivedCategoryReturn1yr.get(f?.category) ?? null;
      },
      /**
       * A scheme's one-year return less its category's average, in points —
       * null when either side is missing, so "no data" never reads as "in line
       * with its peers".
       */
      excessReturn1yr: (f) => {
        const published = f?.snapshot?.categoryReturn1yr;
        const avg = published != null
          ? Number(published)
          : derivedCategoryReturn1yr.get(f?.category);
        if (avg == null || f?.return1yr == null) return null;
        return Number(f.return1yr) - avg;
      },
      /** The unfiltered master — callers join held rows against it by symbol. */
      all,
      /** How many exist in total, before filtering. */
      total: data?.getAllMutualFunds?.total ?? all.length,
      /** Houses that employ the selected manager, or every house. */
      houses,
      /** Managers at the selected house, or everyone. */
      managerNames,
      /** Whether the API published any manager at all — see the note above. */
      hasManagerData: all.some((f) => managersOf(f).length > 0),
    };
  }, [all, data, derivedCategoryReturn1yr, house, manager, search, loading, error]);
}

/**
 * How a fund came to be "yours", most direct first.
 *
 * `firm` classifies the firm's own model portfolios. It earns no section,
 * because a model is not a scheme and `schemes` drops it — it exists so that a
 * model reached this way is still tagged and still resolvable by `kindOf`.
 */
export const FUND_TIERS = [
  ['own', 'Your funds', 'Held in a portfolio you own'],
  ['org', 'Your desk', 'Held by someone in your firm'],
  ['book', 'Under advice', 'Held somewhere in the book you advise'],
  ['firm', 'Firm models', 'Model portfolios and asset-model variants'],
  ['catalogue', 'Not yet linked to you', 'From the scheme master — nothing here holds it yet'],
];

/**
 * The funds this session's user can call theirs.
 *
 * The backend has no fund-manager assignment: nothing links an app user to the
 * schemes they run. `MetaManagerType.managerName` is free text on the scheme
 * master, `Employee` carries org and functional roles but no book, and the only
 * user-to-user edge is organisation membership. So ownership is derived, in
 * tiers of decreasing directness — see FUND_TIERS. A scheme is claimed by the
 * strongest tier that holds it and never appears twice.
 *
 * The last tier exists because this is the first screen a PM sees: when a desk
 * holds nothing yet — or the session cannot read holdings — the fund master
 * fills in, flagged, rather than leaving the screen blank. When the backend
 * grows a real assignment, `own` becomes that query and the ladder below it
 * stays exactly as it is.
 */
export function useMyFunds({ catalogueLimit = 24 } = {}) {
  const book = useBook();
  const org = useOrganization();
  /* Only fetched when nothing above it produced a fund — see `needsCatalogue`. */
  const catalogue = useFunds({ limit: catalogueLimit });

  const derived = useMemo(() => {
    const mine = org.userId;
    const colleagues = new Set(org.memberIds);
    const tierOf = (p) => {
      if (!isClientBook(p)) return 'firm';
      const owner = p.userId == null ? null : String(p.userId);
      if (mine && owner === mine) return 'own';
      if (owner && colleagues.has(owner)) return 'org';
      return 'book';
    };

    /**
     * Which symbols are portfolios in their own right.
     *
     * A client account *holds* the firm's models as line items, so the same
     * symbol is reached twice — once as a holding, once as the model itself —
     * and which comes first is just the order `getPortfolios` answered in.
     * Being a portfolio is the fact that decides how the deep-dive resolves a
     * symbol, so it is settled here, before anything is claimed, rather than
     * left to whichever pass happened to insert the row.
     */
    const modelSymbols = new Set(
      book.all.filter((p) => !isClientBook(p)).map((p) => p.securitySymbol).filter(Boolean),
    );
    /* Everything else in a fund book is a scheme, resolved against the MF master. */
    const kindOf = (symbol) =>
      (modelSymbols.has(symbol) ? SECURITY_KIND.MODEL : SECURITY_KIND.MF);

    const rank = Object.fromEntries(FUND_TIERS.map(([id], i) => [id, i]));
    const bySymbol = new Map();
    const claim = (symbol, name, tier, portfolio, value) => {
      if (!symbol) return;
      const cur = bySymbol.get(symbol);
      if (!cur) {
        bySymbol.set(symbol, {
          symbol,
          name: name || symbol,
          tier,
          kind: kindOf(symbol),
          value: Number(value) || 0,
          portfolios: new Set(portfolio ? [portfolio.securitySymbol] : []),
          holders: new Set(portfolio?.userId != null ? [String(portfolio.userId)] : []),
        });
        return;
      }
      /* Strongest claim wins the label; the totals still count every holder. */
      if (rank[tier] < rank[cur.tier]) cur.tier = tier;
      if (name && cur.name === cur.symbol) cur.name = name;
      cur.value += Number(value) || 0;
      if (portfolio?.securitySymbol) cur.portfolios.add(portfolio.securitySymbol);
      if (portfolio?.userId != null) cur.holders.add(String(portfolio.userId));
    };

    book.all.forEach((p) => {
      const tier = tierOf(p);
      /* A model or fund portfolio is itself a scheme; a client account holds them. */
      if (tier === 'firm') {
        claim(p.securitySymbol, p.portfolioName, 'firm', p, p.marketData?.marketValue);
      }
      (p.holdings ?? EMPTY).forEach((h) =>
        claim(h.holdingSecuritySymbol, h.name, tier, p, h.marketValue));
    });

    return [...bySymbol.values()]
      .map((f) => ({
        ...f,
        portfolios: f.portfolios.size,
        holders: f.holders.size,
      }))
      .sort((a, b) => rank[a.tier] - rank[b.tier] || b.value - a.value);
  }, [book.all, org.userId, org.memberIds]);

  /* Nothing derived means nothing to open — fall back to the master list. */
  const needsCatalogue = !book.loading && !org.loading && derived.length === 0;
  const funds = useMemo(() => {
    if (!needsCatalogue) return derived;
    /* Straight off getAllMutualFunds, so every row here is a scheme. */
    return catalogue.funds.map((f) => ({
      symbol: f.securitySymbol,
      name: f.schemeName || f.fundStandardName || f.securitySymbol,
      tier: 'catalogue',
      kind: SECURITY_KIND.MF,
      value: 0,
      portfolios: 0,
      holders: 0,
      house: f.fundHouse,
      category: f.category,
    }));
  }, [needsCatalogue, derived, catalogue.funds]);

  return useMemo(() => {
    /**
     * Schemes only, by kind rather than by tier.
     *
     * Today every one of these is a model, because `getPortfolios` returns
     * `holdings: []` on all 200 portfolios — a known backend gap. A scheme only
     * ever enters this list as somebody's holding, so with no holdings the
     * ladder derives nothing but the 164 model portfolios themselves, and the
     * largest of them sorted to the front as the book's "first fund".
     *
     * Filtering by tier was not enough to keep that out of a fund screen: it
     * happens to work while `firm` is the only route to a model, but the moment
     * holdings arrive the same model is claimed a second time under the holding
     * account's tier. Kind is settled once, from whether the symbol is itself a
     * portfolio, so it holds either way.
     */
    const schemes = funds.filter((f) => f.kind === SECURITY_KIND.MF);

    const byTier = new Map(FUND_TIERS.map(([id]) => [id, []]));
    schemes.forEach((f) => byTier.get(f.tier)?.push(f));
    return {
      loading: book.loading || org.loading || (needsCatalogue && catalogue.loading),
      error: book.error,
      organisation: org.name,
      /** True when the list is the scheme master rather than anything held. */
      fromCatalogue: needsCatalogue,
      /** Everything reached, models included — what `kindOf` answers from. */
      funds,
      /** The schemes among them: what a fund screen lists and opens. */
      schemes,
      byTier,
      sections: FUND_TIERS
        .map(([id, label, hint]) => ({ id, label, hint, rows: byTier.get(id) ?? EMPTY }))
        .filter((s) => s.rows.length),
      total: schemes.reduce((t, f) => t + f.value, 0),
      /**
       * The kind a symbol was listed as, so a deep-dive opens the right master
       * record. Null while the list is still loading or for a symbol that is
       * not in it — the caller decides what to do with that, rather than being
       * handed a guess.
       */
      kindOf: (symbol) => funds.find((f) => f.symbol === symbol)?.kind ?? null,
    };
  }, [funds, book.loading, book.error, org.loading, org.name, needsCatalogue, catalogue.loading]);
}

/**
 * One scheme's master record and its alpha rank.
 *
 * `mfAlphaForFund` answers with a *list*, not a row: one entry per horizon per
 * run date, so a fund ranked on four horizons over a fortnight of runs comes
 * back as dozens of rows. Handing that array to the screen renders "#undefined"
 * and silences the driver and alternates panels, because every field is read
 * off the array itself. So the latest run is picked here and `rank` stays the
 * single row every caller already expects — with the rest exposed alongside it,
 * since a fund ranked 2nd at 3m and 30th at 12m is information, not noise.
 */
/**
 * A scheme's master-record metadata (flags, minimums, SIP terms, tax, AMC) for
 * the deep-dive's Metadata tab. Read only while that tab is open.
 */
export function useFundMetadata(symbol, { skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_FUND_METADATA, {
    variables: { symbol },
    skip: skip || !symbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return { fund: data?.getMetaMutualFund ?? null, loading, error };
}

export function useFund(symbol, { horizon = DEFAULT_ALPHA_HORIZON, skip = false } = {}) {
  const off = skip || !symbol;
  const meta = useQuery(GET_FUND, { variables: { symbol }, skip: off, fetchPolicy: 'cache-first' });
  const alpha = useQuery(ALPHA_FOR_FUND, {
    variables: { securitySymbol: symbol },
    skip: off,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const rows = alpha.data?.mfAlphaForFund ?? EMPTY;
  const ranks = useMemo(() => {
    if (!rows.length) return EMPTY;
    /* Only the newest run is current; earlier ones are the engine's history. */
    const latest = rows.reduce(
      (best, r) => (String(r.asOfDate || '') > best ? String(r.asOfDate || '') : best),
      '',
    );
    const current = rows.filter((r) => String(r.asOfDate || '') === latest);
    /* A horizon can still repeat across benchmark variants — keep its best rank. */
    const byHorizon = new Map();
    current.forEach((r) => {
      const cur = byHorizon.get(r.horizon);
      if (!cur || Number(r.rank) < Number(cur.rank)) byHorizon.set(r.horizon, r);
    });
    return [...byHorizon.values()].sort(
      (a, b) => Number(String(a.horizon).replace(/\D/g, '')) - Number(String(b.horizon).replace(/\D/g, '')),
    );
  }, [rows]);

  return {
    loading: meta.loading || alpha.loading,
    error: meta.error,
    fund: meta.data?.getMetaMutualFund ?? null,
    /** The requested horizon's row, falling back to the longest one ranked. */
    rank: ranks.find((r) => r.horizon === horizon) ?? ranks[ranks.length - 1] ?? null,
    /** Every horizon the engine ranked this fund on, shortest first. */
    ranks,
  };
}

/**
 * A scheme's NAV series. `skip` exists because the deep-dive also opens model
 * portfolios, and asking the mutual-fund price history about a model can only
 * answer empty — a wasted round-trip that then reads on screen as missing data
 * rather than as a question that does not apply.
 */
export function useFundNavHistory(symbol, filter = '3y', { skip = false } = {}) {
  const { data, loading, error } = useQuery(MF_PRICE_HISTORY, {
    variables: { securitySymbol: symbol, filter },
    skip: skip || !symbol,
    fetchPolicy: 'cache-first',
  });
  const history = data?.getMutualFundPriceHistory?.fundHistory ?? EMPTY;
  return { loading, error, history };
}

/**
 * What the manager traded, disclosure to disclosure.
 *
 * The backend answers oldest first; reversed here, because a desk reads the
 * last filing before it reads the year. Each period keeps its own changes, so
 * moving between periods costs nothing after the one request.
 *
 * A period is labelled by its closing date. `positionsHeld` is the names that
 * did NOT move, which is what makes the four counts beside it add up to the
 * book rather than to the trades.
 *
 * `basis` rides through onto every row: `action` is judged on quantity for most
 * rows and on weight for a few, and the two disagree often enough that a row
 * showing only its weight change would contradict its own label.
 */
export function useFundTrading(securitySymbol, { skip = false } = {}) {
  const { data, loading, error } = useQuery(MF_TRANSACTIONS_HISTORY, {
    variables: { securitySymbol },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const raw = data?.getMutualFundTransactionsHistory?.history ?? EMPTY;
    const periods = [...raw].reverse().map((p) => ({
      ...p,
      /* The trades, biggest move in the book first — a 4bp trim is not the
         answer to "what did they do this month". */
      changes: [...(p.changes ?? EMPTY)]
        .map((c) => ({ ...c, ...(TRADE_ACTION[c.action] ?? { label: c.action, tone: 'grey' }) }))
        .sort((a, b) => Math.abs(Number(b.weightChange) || 0) - Math.abs(Number(a.weightChange) || 0)),
      traded: (p.positionsAdded ?? 0) + (p.positionsExited ?? 0)
        + (p.positionsIncreased ?? 0) + (p.positionsReduced ?? 0),
    }));

    return {
      loading,
      error,
      periods,
      available: periods.length > 0,
    };
  }, [data, loading, error]);
}

/**
 * One security's master record, whichever kind it is.
 *
 * The deep-dive is one screen over two different backends: `getMetaMutualFund`
 * for a scheme, `getPortfolio` for one of the firm's models. Only the query
 * matching the kind is issued, so opening a model never asks the scheme master
 * about it — and `kind` is passed in rather than derived, because the list that
 * produced the symbol already knows it (the catalogue is `getAllMutualFunds`,
 * a model is a portfolio flagged `isModel`).
 *
 * The two records share almost nothing field-for-field, so they are normalised
 * to one view-model here and the screen renders that. Two things this fixes for
 * good: labels travel with their values — a scheme's size is AUM, a model's is
 * the money in it — and returns are brought to one scale, since a scheme's
 * `return1yr` is already a percentage (10.06 = 10.06%) while a portfolio's
 * `oneYearReturn` is a fraction.
 *
 * `portfolio` is the caller's own `usePortfolio` result, reused rather than
 * refetched — it is what a model's record is built from, and the look-through
 * panels need it anyway.
 */
export function useSecurity(symbol, { kind = SECURITY_KIND.MF, portfolio = null } = {}) {
  const isModel = kind === SECURITY_KIND.MODEL;
  const mf = useFund(symbol, { skip: isModel });

  return useMemo(() => {
    if (!isModel) {
      const f = mf.fund;
      const snap = f?.snapshot ?? {};
      return {
        kind,
        loading: mf.loading,
        error: mf.error,
        found: Boolean(f),
        raw: f,
        /** The alpha engine's placing — a composite score, not a return. */
        rank: mf.rank,
        ranks: mf.ranks,
        /**
         * The scheme master's own placing in its category on one-year return.
         *
         * A different measure from `rank` above and routinely a different
         * number — the master puts Bandhan Aggressive Hybrid 7th where the
         * engine puts it 1st — so both are carried, and the screen names which
         * is which. This is the one My Funds lists, so the two agree.
         */
        categoryRank: f?.rankYear1 ?? null,
        categoryRank3y: f?.rankYear3 ?? null,
        name: f?.fundStandardName || f?.fundName || f?.schemeName || symbol,
        /** The full registered name, for the breadcrumb. */
        formalName: f?.schemeName || symbol,
        category: f?.category ?? null,
        issuer: f?.fundHouse ?? null,
        benchmarkName: f?.benchmarkName ?? null,
        size: f?.aum ?? null,
        sizeLabel: 'AUM',
        sizeUnit: 'crore',
        price: snap.nav ?? f?.nav ?? null,
        priceLabel: 'NAV',
        asOfDate: snap.asOfDate ?? f?.inceptionDate ?? null,
        return1y: f?.return1yr ?? null,
        return3y: f?.return3yr ?? null,
        cost: f?.expenseRatio ?? null,
        costLabel: 'Expense / turnover',
        turnover: f?.turnoverRatio ?? null,
        objective: f?.investmentObjective ?? null,
        people: f?.managers?.[0]?.managerName ?? null,
        peopleLabel: 'Manager',
        peopleTenure: f?.managers?.[0]?.managerTenure ?? null,
        risk: {
          alpha: snap.alpha ?? null,
          beta: snap.beta ?? null,
          sharpeRatio: snap.sharpeRatio ?? null,
          volatility: snap.volatility ?? null,
        },
      };
    }

    /* A model's master record is the portfolio itself, which the caller already
       holds — nothing extra is fetched for it. */
    const p = portfolio?.portfolio ?? null;
    const asPct = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v) * 100);
    return {
      kind,
      loading: Boolean(portfolio?.loading),
      error: portfolio?.error ?? null,
      found: Boolean(p),
      raw: p,
      /* A model is not in the ranked universe — the engine ranks schemes, and
         a model has no SEBI category to be placed in either. */
      rank: null,
      ranks: EMPTY,
      categoryRank: null,
      categoryRank3y: null,
      name: p?.portfolioName || symbol,
      formalName: p?.portfolioName || symbol,
      category: p?.portfolioType ? String(p.portfolioType).replace(/_/g, ' ') : null,
      issuer: 'Model portfolio',
      benchmarkName: portfolio?.benchmark?.portfolio?.portfolioName ?? null,
      size: portfolio?.marketValue || null,
      sizeLabel: 'Value',
      sizeUnit: 'rupees',
      price: p?.marketData?.priceClose ?? null,
      priceLabel: 'Unit value',
      asOfDate: p?.latestDataDate ?? p?.marketData?.latestDate ?? null,
      return1y: asPct(portfolio?.returns?.oneYearReturn),
      return3y: asPct(portfolio?.returns?.threeYearReturn),
      cost: null,
      costLabel: 'Expense / turnover',
      turnover: null,
      objective: null,
      people: portfolio?.holdings?.length ? `${portfolio.holdings.length} constituents` : null,
      peopleLabel: 'Composition',
      peopleTenure: null,
      risk: {
        alpha: portfolio?.risk?.alpha ?? null,
        beta: portfolio?.risk?.betaToBenchmark ?? null,
        sharpeRatio: portfolio?.risk?.sharpeRatio ?? null,
        volatility: portfolio?.risk?.volatility ?? null,
      },
    };
  /* Depends on the resolved values rather than on `mf` itself, which useFund
     rebuilds every render. */
  }, [kind, isModel, mf.fund, mf.rank, mf.ranks, mf.loading, mf.error, portfolio, symbol]);
}

/**
 * One fund's scheme master + look-through holdings, for the compare tray.
 *
 * Ranks are NOT fetched here: the whole compare set is ranked in a single
 * `mfAlphaRankUniverse` call by the screen, which also ranks the funds against
 * each other rather than one at a time.
 *
 * Holdings come off the scheme master. This used to ask `getPortfolio` for
 * them, which cannot work — a fund is not a portfolio, so that call answered
 * "Portfolio not found" and the tray compared overlap between two empty lists.
 */
export function useCompared(symbol) {
  const meta = useQuery(GET_FUND, { variables: { symbol }, skip: !symbol, fetchPolicy: 'cache-first' });
  const fund = meta.data?.getMetaMutualFund ?? null;
  return {
    symbol,
    loading: meta.loading,
    fund,
    holdings: fundHoldings(fund),
  };
}

/**
 * Several schemes' latest disclosed holdings at once — for comparing a set of
 * funds (overlap) whose size is the reader's choice, which `useCompared`'s fixed
 * one-hook-per-fund shape cannot hold. The reads go out together through the
 * client, cache-first and shared with every screen that has asked for the same
 * fund, and come back in the order asked.
 */
export function useFundsHoldings(symbols, { skip = false } = {}) {
  const client = useApolloClient();
  const key = (symbols ?? EMPTY).join('|');
  const [state, setState] = useState({ loading: false, funds: EMPTY });

  useEffect(() => {
    const list = key ? key.split('|') : [];
    if (skip || !list.length) {
      setState({ loading: false, funds: EMPTY });
      return undefined;
    }
    let cancelled = false;
    setState((st) => ({ ...st, loading: true }));
    Promise.all(list.map((symbol) => client
      .query({ query: GET_FUND, variables: { symbol }, fetchPolicy: 'cache-first', errorPolicy: 'all' })
      .then(({ data }) => {
        const fund = data?.getMetaMutualFund ?? null;
        return { symbol, fund, holdings: fundHoldings(fund) };
      })
      .catch(() => ({ symbol, fund: null, holdings: EMPTY }))))
      .then((funds) => { if (!cancelled) setState({ loading: false, funds }); });
    return () => { cancelled = true; };
  }, [client, key, skip]);

  return state;
}

/**
 * A scheme's holdings, renamed and rescaled to the portfolio shape.
 *
 * Both records call the position size `weight` and mean different things by it:
 * a portfolio holding's is a fraction (0.10, which `usePortfolio` multiplies up
 * to `weightPct`), a scheme's is already a percentage (10.057). Handing either
 * one straight to a component that expects the other is wrong by a factor of a
 * hundred and looks plausible, so both forms are set here and neither caller
 * has to know which record it was given.
 */
export function fundHoldings(fund) {
  const rows = fund?.holdings ?? EMPTY;
  if (!rows.length) return EMPTY;
  /* The contribution rows are their own list on the record, keyed by symbol —
     joined here so a table row carries its holding's pull on each factor. */
  const contributions = new Map(
    (fund.holdingFactorContributions ?? EMPTY).map((c) => [c.holdingSecuritySymbol, c]),
  );
  return rows
    .map((h) => {
      const pct = Number(h.weight) || 0;
      return {
        holdingSecuritySymbol: h.securitySymbol,
        name: h.name || h.securitySymbol,
        /** Fraction — what overlap and the compare tray read. */
        weight: pct / 100,
        /** Percentage — what the tables display. */
        weightPct: pct,
        marketValue: Number(h.marketValue) || 0,
        quantity: Number(h.quantity) || 0,
        sector: h.sector ?? null,
        /* What this holding contributes to the fund's tilt — its weight times
           its own loading, the backend's product. Null for anything the factor
           model does not cover: cash, repo, government paper, REITs,
           derivatives. The look-through table's Factors view shows these
           rather than the raw loadings, because a contribution reads against
           the position and has a history behind it to chart. */
        contribution: contributions.get(h.securitySymbol) ?? null,
      };
    })
    .sort((a, b) => b.weightPct - a.weightPct);
}

/**
 * A scheme's factor loadings as the bars draw them, dropping any left unscored.
 *
 * Momentum is two bars, not one. The run splits it into a short-term and a
 * long-term score and leaves the combined `momentumScore` null on every record,
 * fund and benchmark alike — so the single Momentum bar this drew before was
 * reading that null and rendering 0.00, which says the fund has no momentum
 * tilt rather than that none was published. The two halves also disagree often
 * enough to be worth seeing apart: HDFC Flexi Cap sits at +0.02 short-term
 * against +0.04 long-term, and its benchmark at −0.05 against +0.08.
 *
 * Unscored now means absent. The guard tests for null before `Number`, since
 * `Number(null)` is 0 and passes `Number.isFinite` — which is what let the
 * empty bar through in the first place.
 *
 * The bars are FACTOR_FIELDS (lib/factors) — one list for a scheme, a model
 * and a client portfolio's look-through alike.
 */
export function fundFactors(exposures) {
  if (!exposures) return EMPTY;
  return Object.entries(FACTOR_FIELDS)
    .map(([label, key]) => ({ label, value: exposures[key] }))
    .filter((x) => x.value != null && Number.isFinite(Number(x.value)));
}

/**
 * Every SEBI category the platform defines, with how many funds the alpha
 * engine ranked in each.
 *
 * The two are NOT the same list and conflating them hides products. The engine
 * skips a category with too few rankable funds — a cross-sectional rank over a
 * handful of funds says more about which funds exist than about any of them —
 * so it covers 29 of the 44 defined. Browsing is a different question from
 * ranking: Index Funds, Gold ETFs and Contra Funds are real things a desk
 * sells, and a category list built from engine coverage leaves them
 * unreachable.
 *
 * So: every category, ranked ones first and largest first, each carrying its
 * ranked count or null. A screen can then show the lot and say which the
 * engine has an opinion about.
 */
export function useSebiCategories(horizon) {
  const { data, loading, error } = useQuery(GET_ASSET_CLASSES_WITH_SEBI, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const engine = useAlphaStatus();

  return useMemo(() => {
    const ranked = new Map(
      (engine.categoriesFor(horizon) ?? []).map((c) => [c.category, c.fundsRanked]),
    );
    const defined = (data?.getAssetClassesWithSebiCategories ?? [])
      .flatMap((a) => (a.sebiCategories ?? []).map((c) => ({
        category: c.name,
        assetClass: a.name,
        fundsRanked: ranked.get(c.name) ?? null,
      })));

    /* A category the engine ranked but the catalogue does not define still
       belongs on screen — the engine is ranking real funds in it. */
    const names = new Set(defined.map((c) => c.category));
    const orphans = [...ranked.entries()]
      .filter(([name]) => !names.has(name))
      .map(([category, fundsRanked]) => ({ category, assetClass: null, fundsRanked }));

    const rows = [...defined, ...orphans].sort((a, b) => {
      if ((a.fundsRanked == null) !== (b.fundsRanked == null)) return a.fundsRanked == null ? 1 : -1;
      if (a.fundsRanked != null) return b.fundsRanked - a.fundsRanked;
      return a.category.localeCompare(b.category);
    });

    return {
      rows,
      rankedCount: rows.filter((r) => r.fundsRanked != null).length,
      loading: loading || engine.loading,
      error,
    };
  }, [data, loading, error, engine, horizon]);
}

/**
 * Fund symbols to the names people read — the fund half of `useSecurityNames`.
 *
 * The scheme master first, since nearly every fund on a route is a scheme.
 * Whatever it leaves unnamed is asked of the ETF master and the model register
 * in one further call: a client portfolio's level-1 holdings are model
 * variants and ETFs as often as schemes, and those used to stay on screen as
 * symbols. That second call is made only when something is left over.
 *
 * Returns a lookup that falls back to the symbol, so an unresolved name never
 * blanks a row; and `loading`, so a drill can hold its first paint until the
 * names are in rather than flash symbols and then replace them.
 */
export function useFundNames(symbols, { skip = false } = {}) {
  const key = (symbols ?? EMPTY).filter(Boolean).join('|');
  const { data, loading } = useQuery(FUNDS_BY_SYMBOLS, {
    variables: { securitySymbols: key ? key.split('|') : EMPTY },
    skip: skip || !key,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const schemes = useMemo(() => new Map(
    (data?.getMutualFundsBySymbols ?? EMPTY).filter((r) => r.schemeName).map((r) => [r.securitySymbol, r.schemeName]),
  ), [data]);
  /* Known only once the scheme master has answered — asked sooner, every
     scheme on the route would be asked of two masters that do not hold it. */
  const restKey = !key || loading ? '' : key.split('|').filter((s) => !schemes.has(s)).join('|');
  const rest = useQuery(ETF_AND_MODEL_NAMES, {
    variables: { securitySymbols: restKey ? restKey.split('|') : EMPTY },
    skip: skip || !restKey,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const bySymbol = new Map(schemes);
    (rest.data?.getEtfsBySymbols ?? EMPTY).forEach((r) => { if (r.schemeName) bySymbol.set(r.securitySymbol, r.schemeName); });
    (rest.data?.getPortfoliosBySymbols ?? EMPTY).forEach((r) => {
      if (r.name && r.name !== r.securitySymbol) bySymbol.set(r.securitySymbol, r.name);
    });
    return { loading: loading || rest.loading, bySymbol, nameOf: (symbol) => bySymbol.get(symbol) || symbol };
  }, [schemes, rest.data, rest.loading, loading]);
}
